import { useCallback, useMemo, useState } from "react";

import { Button } from "devextreme-react/button";
import Box, { Item } from "devextreme-react/box";
import { IItemProps } from "devextreme-react/form";

import { SaveChangeResult } from "../../components/dataGridActionUtil";
import { filterObjectFields } from "../../utils/objectUtil";

import { CrudDataGrid } from "../common/CrudDataGrid";

import { armMappingDimensionColumns, dummyArmMappingDimensionRow } from "./model/metaData";
import { AmrMappingDimension, MappingComponentProps } from "./model/model";
import { ArmMappingDimensionValues } from "./ArmMappingDimensionValues";

type ArmMappingDimensionsProps = MappingComponentProps & {};

export const ArmMappingDimensions = (props: ArmMappingDimensionsProps) => { 
  props.logger.debug("Rendering ArmMappingDimensions");
  const [selectedMapping, setSelectedMapping] = useState<AmrMappingDimension>();
  const [selectedMappingDimensionValues, setSelectedMappingDimensionValues] = useState<string[]>([]);

  const refreshData = useCallback(async () => {
    return props.mappingData.mappingDimensions;
  }, [props.mappingData.mappingDimensions]);

  const filterRowDataForForm = useCallback((data: any) => {
    return filterObjectFields(data, ["values"]);
  }, []);

  const sendChange = async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    switch (change.type) {    
      case "insert":
        change.data.values = [];
        if(change.data.defaultForAllExceptExcluded === undefined) {
          change.data.defaultForAllExceptExcluded = false;
        }
        if(change.data.isExcluding === undefined) {
          change.data.isExcluding = false;
        }
        //Somehow, need this line to change props.mappingData.mappingDimensions array
        //Similar to ArmMappingDimensionValues.tsx explanation
        props.mappingData.mappingDimensions.push(change.data);
        break;
      case "update":
        const uindex = props.mappingData.mappingDimensions
          .findIndex(item => item.mappingDimensionName === change.key);
        if(uindex > -1) {
          const existing = props.mappingData.mappingDimensions[uindex];
          props.mappingData.mappingDimensions[uindex] = {
            ...existing,
            ...change.data,
          };
        }
        break;
      case "remove":
        const index = props.mappingData.mappingDimensions
          .findIndex(item => item.mappingDimensionName === change.key);
        if (index > -1) {
          props.mappingData.mappingDimensions.splice(index, 1);
        }
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: "mappingDimensionName",
    };
  };

  const onSelectionChanged = useCallback((selectedRowsData : any) => {
    const data = selectedRowsData[0];
    setSelectedMapping(data);
    
    // Look up values from mappingDimensionValuesMap
    if (data && props.mappingDimensionValuesMap) {
      const values = props.mappingDimensionValuesMap.get(data.mappingDimensionName) || [];
      setSelectedMappingDimensionValues(values.map((v) => v.value));
    } else {
      setSelectedMappingDimensionValues([]);
    }
  }, [props.mappingDimensionValuesMap]);

  // Get mapping dimension names from the map keys for dropdown options
  const mappingDimensionNames = useMemo(() => {
    if (!props.mappingDimensionValuesMap) {
      return [];
    }
    return Array.from(props.mappingDimensionValuesMap.keys());
  }, [props.mappingDimensionValuesMap]);

  const editorItemOptions = useMemo(() => new Map<string, IItemProps>([
    [
      "mappingDimensionName",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          items: mappingDimensionNames,
          searchEnabled: true,
        },
      },
    ],
    [
      "defaultForAllExceptExcluded",
      {
        editorType: "dxCheckBox",
      },
    ],
    [
      "isExcluding",
      {
        editorType: "dxCheckBox",
      },
    ],
  ]), [mappingDimensionNames]);

  return (
    <div>
      <div>Mapping Dimensions</div>
      <Box direction="row" width="100%">
        <Item ratio={2}>
          <div style={{ paddingRight: "10px" }}>
            <CrudDataGrid
              pageProps={props.pageProps}
              logger={props.logger}
              dummyRow={dummyArmMappingDimensionRow}
              keyExpr={"mappingDimensionName"}
              formTitle="Add Value"
              formColCount={2}
              formColSpan={2}
              refreshData={refreshData}
              allowAdding={true}
              allowUpdating={true}
              allowDeleting={true}
              filterRowDataForForm={filterRowDataForForm}
              sendChange={sendChange}
              hideColumnChooser={true}
              hideSearchPanel={true}
              hideInactiveButton={true}
              columnConfigs={armMappingDimensionColumns}
              selectionMode="single"
              onSelectionChanged={onSelectionChanged}
              editorItemOptions={editorItemOptions}
            ></CrudDataGrid>
          </div>
        </Item>
        <Item ratio={1}>
          <div style={{ paddingLeft: "10px" }}>
            <ArmMappingDimensionValues
              pageProps={props.pageProps}
              logger={props.logger}
              mappingDimension={selectedMapping!}
              availableValues={selectedMappingDimensionValues}
            ></ArmMappingDimensionValues>
          </div>
        </Item>
      </Box>
      <Button
        width={120}
        text="Save"
        type="success"
        stylingMode="contained"
        onClick={async () => {
          props.logger.debug("Mapping Dimensions on saving: " + JSON.stringify(props.mappingData.mappingDimensions));
          if (props.onSaving) {
            await props.onSaving(props.mappingData);
          }
          props.onClosing();
        }}
      />
      <Button
        width={120}
        text="Cancel"
        type="danger"
        stylingMode="contained"
        onClick={() => {
          props.onClosing();
        }}
      />
    </div>
  );
};
